'use client';

import { useStore } from '@/app/providers/store';

/**
 * Claw 业务访问态：
 * - `entitled`：所有用户均可开通，尚未存在 claw 实例
 * - `expired`：claw 实例已过期（claw.status === 'CLAW_STATUS_EXPIRED'）
 * - `ready`：已存在 claw 实例且可以使用（claw.status === 'CLAW_STATUS_ACTIVE'）
 * - `null`：访问态尚未初始化完成
 *
 * 该 hook 仅做派生计算，不与 `useInitClaw` 中的流程进度态（loading / 轮询）重叠。
 */
export type ClawAccess = 'entitled' | 'expired' | 'ready';

interface UseClawAccessResult {
  access: ClawAccess | null;
}

export const useClawAccess = (): UseClawAccessResult => {
  const { userInfo, isUserInfoInitialized, isClawInfoInitialized, hasInstance, clawStatus } = useStore((state) => ({
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    isClawInfoInitialized: state.isClawInfoInitialized,
    hasInstance: !!state.activeClaw?.id,
    clawStatus: state.activeClaw?.status ?? null,
  }));

  // 登录态下需等待 claw 信息初始化完成，避免 access 在首帧被短暂误判后再切换造成闪烁。
  const isAccessInitialized = isUserInfoInitialized && (!userInfo || isClawInfoInitialized);
  if (!isAccessInitialized) {
    return { access: null };
  }

  const isExpired = hasInstance && clawStatus === 'CLAW_STATUS_EXPIRED';
  const access: ClawAccess = isExpired ? 'expired' : hasInstance ? 'ready' : 'entitled';
  return { access };
};
